// types
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { PaymentMethod, PaymentMethodMeta } from "@swiggy-private/payment-types";
import { PaymentGroupData, PaymentGroupMeta, generatePaymentMethodId } from "@swiggy-private/payment-ui";

// helpers
import { isPaymentMethodUpiAndPaymentCodeUpiIntent } from "./UpiHelper";
import { isCODPaymentMethod } from "./CodHelper";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import { syntheticPreferredPaymentGroup } from "@payment-client/constants/SyntheticPPM";
import { getEnabledPaymentMethods, isPaymentGroupEnabled } from "./PaymentHelper";

export const shouldShowSyntheticPPM = (paymentResponse?: GetPaymentMethodsResponse): boolean => {
    if (isEmpty(paymentResponse)) {
        return false;
    }

    const { newUser, xpData } = paymentResponse;
    const xpFlag = xpData?.nuPpmUpi === "true";

    return !!(newUser && xpFlag);
};

export const getAvailablePaymentMethodsFromResponse = (
    group: GetPaymentMethodsResponse["paymentGroup"],
): PaymentMethod<PaymentMethodMeta>[] => {
    const enabledPaymentGroupList = group.filter(pg => isPaymentGroupEnabled(pg));
    const allPaymentMethods = enabledPaymentGroupList.map(pg => pg.paymentMethods);
    const allPaymentMethodsFlattened = allPaymentMethods.reduce((acc, el) => acc.concat(el), []);
    const enabledPaymentMethodsList = getEnabledPaymentMethods(allPaymentMethodsFlattened);

    return enabledPaymentMethodsList;
};

export const getUsablePaymentMethodsForSyntheticPpm = (
    paymentMethods: PaymentMethod<PaymentMethodMeta>[],
    installedUpiIntentApps: string[],
): PaymentMethod<PaymentMethodMeta>[] => {
    const UPIIntentMethods = paymentMethods
        .filter(paymentMethods => isPaymentMethodUpiAndPaymentCodeUpiIntent(paymentMethods))
        .filter(paymentMethod => installedUpiIntentApps.includes(paymentMethod.name));

    const COD = paymentMethods.find(paymentMethod => isCODPaymentMethod(paymentMethod));

    // if COD is available, use COD + any one Upi intent method. Else use at most 2 upi intent methods
    return !isEmpty(COD) ? [COD, ...UPIIntentMethods].slice(0, 2) : UPIIntentMethods.slice(0, 2);
};

const filterPaymentMethodsInPaymentGroups = (
    paymentGroup: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>[],
    filterables: string[],
): PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>[] => {
    try {
        return paymentGroup.map(group => ({
            ...group,
            paymentMethods: group.paymentMethods.filter(
                method => !filterables.includes(generatePaymentMethodId(method)),
            ),
        }));
    } catch {
        return paymentGroup;
    }
};

export const transformResponseForNuPPm = (
    paymentResponse: GetPaymentMethodsResponse,
    availableUpiIntentApps: string[],
): GetPaymentMethodsResponse => {
    const { paymentGroup } = paymentResponse;

    if (!shouldShowSyntheticPPM(paymentResponse)) {
        return paymentResponse;
    }

    const allEnabledMethodsInResponse = getAvailablePaymentMethodsFromResponse(paymentGroup);
    const paymentMethodsForSyntheticGroup = getUsablePaymentMethodsForSyntheticPpm(
        allEnabledMethodsInResponse,
        availableUpiIntentApps || [],
    );

    if (isEmpty(paymentMethodsForSyntheticGroup)) {
        return paymentResponse;
    }

    const paymentMethodIdsUsedInSyntheticPaymentGroup = paymentMethodsForSyntheticGroup.map(method =>
        generatePaymentMethodId(method),
    );

    return {
        ...paymentResponse,
        paymentGroup: [
            // Inject synthetic payment group into payment group as first element
            {
                ...syntheticPreferredPaymentGroup,
                paymentMethods: paymentMethodsForSyntheticGroup,
            },
            // Filter out any method used in synthetic payments from other payment groups to eliminate duplication
            ...filterPaymentMethodsInPaymentGroups(paymentGroup, paymentMethodIdsUsedInSyntheticPaymentGroup),
        ],
    };
};
